import { createContext, useContext, useMemo, useState } from "react";
import { InferenceFiltersProps } from "../types/inferences";

const InferenceFiltersContext = createContext<InferenceFiltersProps | null>(
  null,
);

type Props = {
  children: React.ReactNode;
};

type BooleanUndefined = boolean | undefined;

export function InferenceFiltersProvider({ children }: Props) {
  const [isWhale, setIsWhale] = useState<boolean | undefined>(true);
  const [isValid, setIsValid] = useState<boolean | undefined>(undefined);
  const [inferencesValidated, setInferencesValidated] =
    useState<BooleanUndefined>(undefined);
  const [minConfidence, setMinConfidence] = useState<number>(0.5);
  const [minCoastlineDistance, setMinCoastlineDistance] = useState<number>(100);
  const [isFlagged, setIsFlagged] = useState<BooleanUndefined>(undefined);
  const [activeSceneId, setActiveSceneId] = useState<string>("");
  const [compositeMode, setCompositeMode] = useState<boolean>(true);

  const resetFilters = () => {
    setIsValid(undefined);
    setInferencesValidated(undefined);
    setIsWhale(true);
    setMinConfidence(0.5);
    setMinCoastlineDistance(100);
    setIsFlagged(undefined);
  };

  const contextValue = {
    isWhale,
    setIsWhale,
    isValid,
    setIsValid,
    inferencesValidated,
    setInferencesValidated,
    minConfidence,
    setMinConfidence,
    minCoastlineDistance,
    setMinCoastlineDistance,
    isFlagged,
    setIsFlagged,
    activeSceneId,
    setActiveSceneId,
    resetFilters,
    compositeMode,
    setCompositeMode,
  };

  return (
    <InferenceFiltersContext.Provider value={contextValue}>
      {children}
    </InferenceFiltersContext.Provider>
  );
}

export function useInferenceFiltersContext() {
  const {
    isWhale,
    setIsWhale,
    isValid,
    setIsValid,
    inferencesValidated,
    setInferencesValidated,
    minConfidence,
    setMinConfidence,
    minCoastlineDistance,
    setMinCoastlineDistance,
    isFlagged,
    setIsFlagged,
    activeSceneId,
    setActiveSceneId,
    resetFilters,
    compositeMode,
    setCompositeMode,
  } = (useContext(InferenceFiltersContext) as InferenceFiltersProps) || {};

  return useMemo(() => {
    return {
      isWhale,
      setIsWhale,
      isValid,
      setIsValid,
      inferencesValidated,
      setInferencesValidated,
      minConfidence,
      setMinConfidence,
      minCoastlineDistance,
      setMinCoastlineDistance,
      isFlagged,
      setIsFlagged,
      activeSceneId,
      setActiveSceneId,
      resetFilters,
      compositeMode,
      setCompositeMode,
    };
  }, [
    isWhale,
    setIsWhale,
    isValid,
    setIsValid,
    inferencesValidated,
    setInferencesValidated,
    minConfidence,
    setMinConfidence,
    minCoastlineDistance,
    setMinCoastlineDistance,
    isFlagged,
    setIsFlagged,
    activeSceneId,
    setActiveSceneId,
    resetFilters,
    compositeMode,
    setCompositeMode,
  ]);
}
